import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Star, MessageSquare, TrendingUp, Users, Loader2, Smile, Frown } from 'lucide-react';
import { toast } from 'sonner';

const FLAG_LABELS = {
  safety_critical: { label: 'Safety alert', className: 'bg-red-100 text-red-700 border-red-200' },
  maintenance: { label: 'Maintenance / cleanliness', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  service_fault: { label: 'Service fault', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  low_rating: { label: 'Low rating', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  warm_lead: { label: 'Warm lead', className: 'bg-green-100 text-green-700 border-green-200' },
};
const READINESS_LABELS = { yes_fully: 'Ready: yes, fully', mostly: 'Ready: mostly', no: 'Ready: no' };
const RECOMMEND_LABELS = ['Very unlikely', 'Unlikely', 'Undecided', 'Likely', 'Very likely'];
const SAFETY_LABELS = {
  cleanliness: 'Cleanliness', maintenance: 'Maintenance or damage', fire_safety: 'Fire safety',
  electrical_hazard: 'Electrical / physical hazard', security: 'Security / belongings', other: 'Other',
};
const RATING_ROWS = [
  ['rating_booking', 'Booking & check-in'], ['rating_staff', 'Staff'], ['rating_cleanliness', 'Cleanliness'],
  ['rating_comfort', 'Comfort'], ['rating_wifi', 'Wi-Fi'], ['rating_av', 'Room & AV'], ['rating_value', 'Value'],
];

const StarsDisplay = ({ value }) => (
  <div className="inline-flex items-center gap-0.5" data-testid="stars-display">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} className={`w-3.5 h-3.5 ${s <= value ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} />
    ))}
  </div>
);

export const FeedbackTab = ({ api, venues, isAdminOrHM }) => {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [venueFilter, setVenueFilter] = useState('all');
  const [period, setPeriod] = useState('30');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (venueFilter !== 'all') params.venue_id = venueFilter;
      if (period !== 'all') params.days = parseInt(period);
      const [s, list] = await Promise.all([
        api.get('/feedback/stats', { params }),
        api.get('/feedback', { params }),
      ]);
      setStats(s.data);
      setItems(Array.isArray(list.data) ? list.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [api, venueFilter, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feedback?')) return;
    try {
      await api.delete(`/feedback/${id}`);
      toast.success('Feedback deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const ratingPct = (val) => Math.max(0, Math.min(100, (val / 5) * 100));

  return (
    <div className="space-y-6" data-testid="feedback-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-heading">Feedback & Experience</h2>
          <p className="text-sm text-muted-foreground">Post-booking ratings and comments from clients across your centres.</p>
        </div>
        <div className="flex gap-2">
          <Select value={venueFilter} onValueChange={setVenueFilter}>
            <SelectTrigger className="w-[200px]" data-testid="feedback-venue-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Centres</SelectItem>
              {(venues || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]" data-testid="feedback-period-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Star className="w-3 h-3" /> Overall Avg</p>
              <p className="text-3xl font-bold mt-1" data-testid="feedback-avg-overall">{stats?.avg_overall || 0}<span className="text-base text-muted-foreground">/5</span></p>
              <StarsDisplay value={Math.round(stats?.avg_overall || 0)} />
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Responses</p>
              <p className="text-3xl font-bold mt-1" data-testid="feedback-total">{stats?.total || 0}</p>
              <p className="text-xs text-muted-foreground">of {stats?.eligible_bookings || 0} eligible bookings</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Response Rate</p>
              <p className="text-3xl font-bold mt-1">{stats?.response_rate || 0}%</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Smile className="w-3 h-3 text-green-500" /> Promoters</p>
                  <p className="text-2xl font-bold mt-1 text-green-600">{stats?.promoters || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Frown className="w-3 h-3 text-red-500" /> Detractors</p>
                  <p className="text-2xl font-bold mt-1 text-red-600">{stats?.detractors || 0}</p>
                </div>
              </div>
            </CardContent></Card>
          </div>

          {/* Readiness / repeat intent / escalations (guest experience form) */}
          {(stats?.readiness_yes_pct != null || Object.keys(stats?.escalation_counts || {}).length > 0) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="feedback-ops-kpis">
              <Card><CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Everything ready as booked</p>
                <p className="text-2xl font-bold mt-1">{stats?.readiness_yes_pct != null ? `${stats.readiness_yes_pct}%` : '–'}</p>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Likely to return / recommend</p>
                <p className="text-2xl font-bold mt-1">{stats?.recommend_likely_pct != null ? `${stats.recommend_likely_pct}%` : '–'}</p>
              </CardContent></Card>
              <Card className="md:col-span-2"><CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-2">Needs attention</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(stats?.escalation_counts || {}).map(([k, n]) => (
                    <Badge key={k} variant="outline" className={`text-xs ${FLAG_LABELS[k]?.className || ''}`}>
                      {FLAG_LABELS[k]?.label || k}: {n}
                    </Badge>
                  ))}
                  {Object.keys(stats?.escalation_counts || {}).length === 0 && <span className="text-sm text-muted-foreground">Nothing flagged</span>}
                </div>
              </CardContent></Card>
            </div>
          )}

          {/* Per-question averages */}
          <Card>
            <CardContent className="p-5 space-y-4">
              <h3 className="font-heading text-lg">By Category</h3>
              {[
                { label: 'Overall experience', val: stats?.avg_overall },
                { label: 'Booking & check-in', val: stats?.avg_booking },
                { label: 'Reception & staff support', val: stats?.avg_staff },
                { label: 'Cleanliness & upkeep', val: stats?.avg_cleanliness },
                { label: 'Space & comfort', val: stats?.avg_comfort },
                { label: 'Wi-Fi & connectivity', val: stats?.avg_wifi },
                { label: 'Meeting room setup & AV', val: stats?.avg_av },
                { label: 'Value for money', val: stats?.avg_value },
              ].filter((row) => row.val != null).map((row) => (
                <div key={row.label} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span>{row.label}</span>
                    <div className="flex items-center gap-2">
                      <StarsDisplay value={Math.round(row.val)} />
                      <span className="font-mono text-xs w-10 text-right">{row.val.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-amber-400 transition-all" style={{ width: `${ratingPct(row.val)}%` }} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Per-venue (only useful when venueFilter === all and multiple centres) */}
          {(stats?.by_venue?.length || 0) > 1 && (
            <Card>
              <CardContent className="p-5 space-y-3">
                <h3 className="font-heading text-lg">By Centre</h3>
                <div className="space-y-2">
                  {stats.by_venue.map((v) => (
                    <div key={v.venue_id} className="flex items-center justify-between p-2 rounded-md hover:bg-muted/40">
                      <div>
                        <p className="font-medium text-sm">{v.venue_name}</p>
                        <p className="text-xs text-muted-foreground">{v.count} response(s)</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StarsDisplay value={Math.round(v.avg_overall)} />
                        <span className="font-mono text-xs w-10 text-right">{v.avg_overall.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent feedback list */}
          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="font-heading text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" /> Recent Feedback
              </h3>
              {items.length === 0 ? (
                <div className="py-10 text-center text-muted-foreground">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 text-muted-foreground/40" />
                  <p>No feedback yet. Emails are sent 30 minutes after each booking ends.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((f) => (
                    <div key={f.id} className="border rounded-lg p-4 space-y-2" data-testid={`feedback-item-${f.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-0.5 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{f.user_name || 'Guest'}</span>
                            <Badge variant="outline" className="text-xs">{f.venue_name}</Badge>
                            <Badge variant="outline" className="text-xs">{f.bookable_name}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">{f.user_email} · {new Date(f.submitted_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                        </div>
                        <StarsDisplay value={f.rating_overall} />
                      </div>
                      {(f.escalation_flags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {f.escalation_flags.map((fl) => (
                            <Badge key={fl} variant="outline" className={`text-xs ${FLAG_LABELS[fl]?.className || ''}`}>
                              {FLAG_LABELS[fl]?.label || fl}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                        {f.readiness ? (
                          RATING_ROWS.filter(([k]) => f[k] != null).map(([k, label]) => (
                            <span key={k} className="text-muted-foreground">{label}: <span className="font-mono font-semibold text-foreground">{f[k]}/5</span></span>
                          ))
                        ) : (
                          <>
                            <span className="text-muted-foreground">Cleanliness: <span className="font-mono font-semibold text-foreground">{f.rating_cleanliness}/5</span></span>
                            <span className="text-muted-foreground">Amenities: <span className="font-mono font-semibold text-foreground">{f.rating_amenities}/5</span></span>
                            <span className="text-muted-foreground">Staff: <span className="font-mono font-semibold text-foreground">{f.rating_staff}/5</span></span>
                          </>
                        )}
                      </div>
                      {f.readiness && (
                        <p className="text-xs text-muted-foreground">
                          {READINESS_LABELS[f.readiness]}
                          {f.recommend ? ` · Return/recommend: ${RECOMMEND_LABELS[f.recommend - 1]}` : ''}
                        </p>
                      )}
                      {f.readiness_issue && <p className="text-sm bg-muted/40 rounded p-2">What went wrong: {f.readiness_issue}</p>}
                      {f.safety_concern && (
                        <p className="text-sm bg-red-50 text-red-800 rounded p-2">
                          Concern: {(f.safety_categories || []).map((c) => SAFETY_LABELS[c] || c).join(', ') || 'unspecified'}
                          {f.safety_details ? ` — ${f.safety_details}` : ''}
                        </p>
                      )}
                      {f.liked && <p className="text-sm bg-muted/40 rounded p-2">Did well: "{f.liked}"</p>}
                      {f.improve && <p className="text-sm bg-muted/40 rounded p-2">Could be better: "{f.improve}"</p>}
                      {!f.readiness && f.comments && (
                        <p className="text-sm bg-muted/40 rounded p-2 mt-1">"{f.comments}"</p>
                      )}
                      {f.contact_ok && (
                        <p className="text-xs text-muted-foreground">
                          OK to contact: {[f.contact_name, f.contact_info].filter(Boolean).join(' · ') || f.user_email}
                        </p>
                      )}
                      {isAdminOrHM && (
                        <div className="flex justify-end pt-1">
                          <button onClick={() => handleDelete(f.id)} className="text-xs text-red-500 hover:underline" data-testid={`feedback-delete-${f.id}`}>Delete</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default FeedbackTab;
